import { type AnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import { useState } from 'react';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import { useDebouncedValue } from '../../../../hooks';
import { type GetCoreProductsRequest } from '../../../../redux/services';
import { useGetCoreProductsQuery } from '../../../../redux/services/core';
import { TailwindFormField } from '../../../../tailwind';
import { MultiSelectDropdown } from '../../../multi-select-dropdown';
import {
  isProductDisabled,
  MultiselectProductItem,
} from '../../CoreApiMultiselectItems';
import { useMultiselectApiFilters } from '../../hooks';
import { type GetMultiselectMapper } from '../../types';
import {
  SERVER_MIN_SEARCH_TERM_LENGTH,
  SERVER_SIDE_SEARCH_DROPDOWN_LIMIT,
} from '../../utilities';
import { getRelevantCoreProductFilterKeys } from '../utilities/core-product-filter-keys';

type ProductFilters = GetCoreProductsRequest['body']['filters'];

interface Props<T extends FieldValues> {
  disabled?: boolean;
  control: Control<T>;
  name: Path<T>;
  catalogueMap?: GetMultiselectMapper<ProductFilters['catalogue'], T>;
  productMap?: GetMultiselectMapper<ProductFilters['product'], T>;
  productGroupsMap?: GetMultiselectMapper<ProductFilters['product_groups'], T>;
  locationClustersMap?: GetMultiselectMapper<
    ProductFilters['location_cluster'],
    T
  >;
  analyticsEvent?: AnalyticsEvent;
}

export const CoreProductsMultiselect = <T extends FieldValues>({
  control,
  name,
  catalogueMap,
  productMap,
  productGroupsMap,
  locationClustersMap,
  analyticsEvent,
  disabled,
}: Props<T>) => {
  const catalogueFilters = useMultiselectApiFilters({
    control,
    map: catalogueMap,
    relevantFilterKeys: ['events', 'seasons'],
  });

  const productFilters = useMultiselectApiFilters({
    control,
    map: productMap,
    relevantFilterKeys: getRelevantCoreProductFilterKeys([]),
  });

  const productGroupsFilters = useMultiselectApiFilters({
    control,
    map: productGroupsMap,
    relevantFilterKeys: ['groups'],
  });

  const locationClustersFilters = useMultiselectApiFilters({
    control,
    map: locationClustersMap,
    relevantFilterKeys: ['location_clusters'],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue({
    value:
      searchTerm.length < SERVER_MIN_SEARCH_TERM_LENGTH ? null : searchTerm,
    defaultValue: null,
  });

  const { common } = useAutoneTranslation();

  const { data, isLoading } = useGetCoreProductsQuery({
    query: {
      limit: SERVER_SIDE_SEARCH_DROPDOWN_LIMIT,
    },
    body: {
      filters: {
        product: {
          search_term: debouncedSearchTerm,
          ...productFilters,
        },
        catalogue: catalogueFilters,
        product_groups: productGroupsFilters,
        location_cluster: locationClustersFilters,
        size_run: {},
        size: {},
      },
    },
  });

  const options = data?.items ?? [];

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <MultiSelectDropdown
          disabled={disabled}
          className="h-fit"
          loading={isLoading}
          options={options}
          getOptionId={(option) => option.id}
          getOptionLabel={(option) =>
            option?.style_desc ?? common('not-available')
          }
          onSearchTermChange={setSearchTerm}
          selected={value ?? []}
          onSelectedChange={(ids) => onChange(ids.length ? ids : null)}
          renderOption={(product) => <MultiselectProductItem {...product} />}
          analyticsEvent={analyticsEvent}
          isOptionDisabled={isProductDisabled}
        />
      )}
    />
  );
};
